import type { JSX } from "react";

import { Text, Transition } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import clsx from "clsx";
import { useEffect, useState } from "react";

import { ActivityBubble } from "@/common-ui";

import { AutoAvailableCountdown } from "./AutoAvailableCountdown";
import styles from "./autoAvailablePill.module.css";
import { type AutoAvailablePhase } from "./useAutoAvailableCountdown";

interface Props {
    active: boolean;
    phase: AutoAvailablePhase;
    countdownMs: number;
    onExpire: () => void;
    isVoice: boolean;
}

type PillContent = "countdown" | "waiting";

const FADE_MS = 300;
const SWAP_FADE_MS = 200;

export function AutoAvailablePill({ active, phase, countdownMs, onExpire, isVoice }: Props): JSX.Element {
    const target: PillContent = phase === "waiting" ? "waiting" : "countdown";
    const reducedMotion = useReducedMotion();

    const [displayed, setDisplayed] = useState<PillContent>(target);
    const [swapping, setSwapping] = useState(false);

    useEffect(() => {
        // While inactive (fading out) freeze the content, so the exit shows the last state instead
        // of flipping back to the countdown.
        if (!active || target === displayed) return;
        if (reducedMotion) {
            setDisplayed(target);
            setSwapping(false);
            return;
        }
        setSwapping(true);
        const timer = setTimeout(() => {
            setDisplayed(target);
            setSwapping(false);
        }, SWAP_FADE_MS);
        return () => clearTimeout(timer);
    }, [active, target, displayed, reducedMotion]);

    const waiting = displayed === "waiting";

    return (
        <Transition mounted={active} transition="fade" duration={FADE_MS} timingFunction="ease">
            {(fadeStyle) => (
                <div style={fadeStyle} className={styles.container}>
                    <div className={clsx(styles.pill, waiting && styles.pillWaiting, swapping && styles.pillSwapping)}>
                        {waiting ? (
                            <>
                                <ActivityBubble variant="available" />
                                <Text className={styles.label}>
                                    {isVoice ? "Waiting for a call" : "Waiting for chats"}
                                </Text>
                            </>
                        ) : (
                            <AutoAvailableCountdown durationMs={countdownMs} onTimeout={onExpire} isVoice={isVoice} />
                        )}
                    </div>
                </div>
            )}
        </Transition>
    );
}
